import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Container,
  Stack,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Security, QrCode, CheckCircle, Warning } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { supabase } from '../../supabaseClient';
import { useTheme } from '../../ThemeContext';
import QRCode from 'qrcode';

const MandatoryTwoFactorSetup = ({ user, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [factorId, setFactorId] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const { theme } = useTheme();

  // Start verplichte 2FA setup
  const startMandatorySetup = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Enroll een nieuwe TOTP factor
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: `${user.email} - Authenticator App`
      });
      
      if (error) throw error;
      
      setFactorId(data.id);
      setSecret(data.totp.secret);
      
      // Genereer QR code voor de secret
      const otpauth = `otpauth://totp/BEM.report:${user.email}?secret=${data.totp.secret}&issuer=BEM.report`;
      const qrUrl = await QRCode.toDataURL(otpauth);
      setQrCodeUrl(qrUrl);
      
      setShowSetupDialog(true);
    } catch (error) {
      console.error('Error starting mandatory 2FA setup:', error);
      setError(`Fout bij instellen 2FA: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Verificeer 2FA code en voltooi verplichte setup
  const verifyAndComplete = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!verifyCode || verifyCode.length !== 6) {
        throw new Error('Voer een geldige 6-cijferige code in');
      }
      
      // Maak een challenge voor verificatie
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: factorId
      });
      
      if (challengeError) throw challengeError;
      
      // Verificeer de code
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: factorId,
        challengeId: challengeData.id,
        code: verifyCode
      });
      
      if (verifyError) throw verifyError;
      
      // Setup voltooid! Ga naar normale app
      onComplete();
      
    } catch (error) {
      console.error('Error verifying mandatory 2FA code:', error);
      setError(`Verificatie mislukt: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delay: 0.2
      }
    }
  };

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.primary.light}20 50%, ${theme.palette.primary.main}10 100%)`,
          p: 2,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23000000" fill-opacity="0.02"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3,
          }
        }}
      >
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={cardVariants}>
              <Paper 
                elevation={8} 
                sx={{ 
                  p: { xs: 3, sm: 4 }, 
                  width: '100%', 
                  borderRadius: 4,
                  background: (theme) => theme.palette.mode === 'dark' 
                    ? 'rgba(0, 0, 0, 0.6)' 
                    : 'rgba(255, 255, 255, 0.85)',
                  backdropFilter: 'blur(20px)',
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  boxShadow: (theme) => `0 20px 40px -12px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.15)'}`,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: `linear-gradient(90deg, ${theme.palette.warning.main} 0%, ${theme.palette.error.main} 100%)`,
                  }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                  <motion.div
                    whileHover={{ scale: 1.05, rotate: 2 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        mb: 2,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.error.main} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 8px 20px ${theme.palette.warning.main}40`,
                      }}
                    >
                      <Warning sx={{ fontSize: 40, color: 'white' }} />
                    </Box>
                  </motion.div>
                  <Typography variant="h4" component="h1" sx={{ 
                    fontWeight: 'bold',
                    background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.error.main} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1,
                    textAlign: 'center'
                  }}>
                    Beveiliging Vereist
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.secondary",
                      textAlign: 'center',
                      fontWeight: 500,
                      lineHeight: 1.6
                    }}>
                    Je moet twee-factor authenticatie instellen om door te gaan
                  </Typography>
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                <Stack spacing={3}>
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Waarom is dit vereist?
                    </Typography>
                    <Typography variant="body2">
                      Voor de veiligheid van je account en gegevens is twee-factor authenticatie verplicht. 
                      Dit duurt slechts een paar minuten om in te stellen.
                    </Typography>
                  </Alert>

                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Wat heb je nodig?
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{
                        justifyContent: "center",
                        flexWrap: "wrap"
                      }}>
                      <Box sx={{ textAlign: 'center', minWidth: 120 }}>
                        <Security sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                        <Typography variant="body2">
                          Authenticator App
                        </Typography>
                        <Typography variant="caption" sx={{
                          color: "text.secondary"
                        }}>
                          Google Authenticator, 1Password, etc.
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', minWidth: 120 }}>
                        <QrCode sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                        <Typography variant="body2">
                          QR Code Scannen
                        </Typography>
                        <Typography variant="caption" sx={{
                          color: "text.secondary"
                        }}>
                          Koppel je app aan je account
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  <Divider />

                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Security />}
                      onClick={startMandatorySetup}
                      disabled={loading}
                      sx={{
                        py: 1.5,
                        px: 4,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        '&:hover': {
                          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                          boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
                        }
                      }}
                    >
                      {loading ? 'Bezig met instellen...' : '2FA Nu Instellen'}
                    </Button>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 2,
                        color: 'text.secondary'
                      }}>
                      Dit is een eenmalige setup en duurt ongeveer 2 minuten
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </motion.div>
          </motion.div>
        </Container>
      </Box>

      {/* Setup Dialog */}
      <Dialog 
        open={showSetupDialog} 
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderRadius: 3 }
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Stack direction="column" spacing={1} sx={{
            alignItems: "center"
          }}>
            <QrCode color="primary" sx={{ fontSize: 40 }} />
            <Typography variant="h6">Verplichte 2FA Setup</Typography>
          </Stack>
        </DialogTitle>
        
        <DialogContent>
          <Stack spacing={3}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Scan de QR code met je authenticator app en voer de 6-cijferige code in om de setup te voltooien.
            </Alert>
            
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Scan deze QR code:
              </Typography>
              {qrCodeUrl && (
                <Box 
                  component="img" 
                  src={qrCodeUrl} 
                  alt="2FA QR Code"
                  sx={{ 
                    maxWidth: '100%', 
                    height: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                    bgcolor: 'background.paper'
                  }}
                />
              )}
            </Box>
            
            <Divider />
            
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Of voer deze code handmatig in:
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  p: 1,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  wordBreak: 'break-all'
                }}
              >
                {secret}
              </Typography>
            </Box>
            
            <TextField
              fullWidth
              label="Verificatie code (6 cijfers)"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              helperText="Voer de 6-cijferige code uit je authenticator app in"
              slotProps={{
                htmlInput: {
                  maxLength: 6,
                  style: { textAlign: 'center', fontSize: '1.2rem', letterSpacing: '0.3em' }
                }
              }}
            />
            
            
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={verifyAndComplete}
            variant="contained"
            fullWidth
            disabled={loading || verifyCode.length !== 6}
            startIcon={loading ? <CircularProgress size={16} /> : <CheckCircle />}
            sx={{ py: 1.5 }}
          >
            {loading ? 'Verificeren...' : 'Setup Voltooien'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MandatoryTwoFactorSetup;
